import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Object3D } from '@wonderlandengine/api';
import { ColourDescription } from './components/colour-description.js';
import { PaintDescription } from './components/paint-description.js';
import { MaterialDescription } from './components/material-description.js';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';
import { Configuration, HardTopOptions, AccentPackOptions } from '../data/configuration.js';
import { CarPbrMaterial, cloneBodyPaintMaterial } from './paint-configurator.ts'

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface GrilleMaterial extends Material {
	albedoColor: NumberArray;
}

type FadableMaterial = Material | GrilleMaterial | CarPbrMaterial;
type FadableKey = {
	[TK in keyof FadableMaterial]: FadableMaterial[TK] extends NumberArray ? TK : never;
}[keyof FadableMaterial];

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;

/**
 * Exterior Configurator:
 */
export class ExteriorConfigurator extends Component {
	static TypeName = 'exterior-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	hoodDescriptions!: Object3D;

	@property.object({ required: true })
	paintDescriptions!: Object3D;

	@property.object({ required: true })
	softTopUp!: Object3D;

	@property.object({ required: true })
	softTopDown!: Object3D;

	@property.object({ required: true })
	hardTop!: Object3D;

	@property.object({ required: true })
	hardTopExterior!: Object3D;
	
	@property.object({ required: true })
	sideWindows!: Object3D;

	@property.object({ required: true })
	exteriorSideScreen!: Object3D;

	@property.object({ required: true })
	sideScreenSilver!: Object3D;

	@property.object({ required: true })
	sideScreenBlack!: Object3D;

	@property.object({ required: true })
	cowlEmbelisherSilver!: Object3D;

	@property.object({ required: true })
	cowlEmbelisherBlack!: Object3D;

	@property.object({ required: true })
	frontOverridersSilver!: Object3D;

	@property.object({ required: true })
	frontOverridersBlack!: Object3D;

	@property.object({ required: true })
	rearOverridersSilver!: Object3D;

	@property.object({ required: true })
	rearOverridersBlack!: Object3D;

	@property.object({ required: true })
	doorRoundelDescriptions!: Object3D;

	@property.object({ required: true })
	doorRoundel!: Object3D;

	@property.object({ required: true })
	licensePlateUsFront!: Object3D;

	@property.object({ required: true })
	s400GraphicsDescriptions!: Object3D;

	@property.object({ required: true })
	bonnetBootStripes400!: Object3D;

	@property.object({ required: true })
	sillGraphics400!: Object3D;

	@property.object({ required: true })
	rearDiffuserGraphic400!: Object3D;

	@property.material({ required: true })
	sophistoGreyPaintMaterial400!: Material;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _hoodDescriptions!: ColourDescription[];
	private _paintDescriptions!: PaintDescription[];
	private _softTopUp!: MeshComponent;
	private _softTopDown!: MeshComponent;
	private _hardTopExterior!: MeshComponent;
	private _exteriorSideScreen!: MeshComponent;
	private _doorRoundelDescriptions!: MaterialDescription[];
	private _doorRoundel!: MeshComponent;
	private _s400GraphicsDescriptions!: MaterialDescription[];
	private _bonnetBootStripes400!: MeshComponent;
	private _sillGraphics400!: MeshComponent;

	private _tweenManager = new TweenManager();
	
	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start() {
		this._hoodDescriptions = this.hoodDescriptions.getComponents(ColourDescription)!;
		this._paintDescriptions = this.paintDescriptions.getComponents(PaintDescription)!;
		this._softTopUp = this.softTopUp.getComponent(MeshComponent)!;
		this._softTopDown = this.softTopDown.getComponent(MeshComponent)!;
		this._hardTopExterior = this.hardTopExterior.getComponent(MeshComponent)!;
		this._exteriorSideScreen = this.exteriorSideScreen.getComponent(MeshComponent)!;
		this._doorRoundelDescriptions = this.doorRoundelDescriptions.getComponents(MaterialDescription)!;
		this._doorRoundel = this.doorRoundel.getComponent(MeshComponent)!;
		this._s400GraphicsDescriptions = this.s400GraphicsDescriptions.getComponents(MaterialDescription)!;
		this._bonnetBootStripes400 = this.bonnetBootStripes400.getComponent(MeshComponent)!;
		this._sillGraphics400 = this.sillGraphics400.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Selects the soft top state, putting it up or down by updating the relevant meshes/materials on the vehicle model.
	*/
	selectSoftTopState(hoodUp: boolean) {

		// if hardtop only, disable soft top completely.
		if (Configuration.isHardTopOnly(Configuration.hardTopOption)) {
			Utilities.setActiveAll(this.softTopUp, false);
			Utilities.setActiveAll(this.softTopDown, false);
			return;
		}

		// if soft top is being activated while Mohair options are selected, deactivate hard top.
		if (Configuration.isHardTopAndSoftTop(Configuration.hardTopOption)) {
			Utilities.setActiveAll(this.hardTop, false);
		}

		// show soft top with hood up or down depending on hoodUp state.
		Utilities.setActiveAll(this.softTopUp, hoodUp);
		Utilities.setActiveAll(this.softTopDown, !hoodUp);
	}

	/**
	* Selects the hard top state, putting it up or down by updating the relevant meshes/materials on the vehicle model.
	*/
	selectHardTopState(hardTopVisible: boolean, hoodUp: boolean, imageOverride?: boolean) {

		// initially hide hard top.
		Utilities.setActiveAll(this.hardTop, false);

		// if hardtop only, enable hardtop (unless overriding).
		if (Configuration.isHardTopOnly(Configuration.hardTopOption) && !imageOverride) {
			Utilities.setActiveAll(this.hardTop, true);
			return;
		}

		// if soft top is being activated while Mohair options are selected, deactivate hard top based in visibility flag,
		// and show soft top with hood up or down depending on hoodUp state.
		if (Configuration.isHardTopAndSoftTop(Configuration.hardTopOption)) {
			Utilities.setActiveAll(this.hardTop, hardTopVisible);
			if (!hardTopVisible) {
				Utilities.setActiveAll(this.softTopDown, !hoodUp);
				Utilities.setActiveAll(this.softTopUp, hoodUp);
			} else {
				Utilities.setActiveAll(this.softTopDown, false);
				Utilities.setActiveAll(this.softTopUp, false);
			}
		}
	}
	
	/**
	* Selects a hard top style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectHardTop(id: string, hoodUp: boolean) {

		// if a grey hard top is selected (Supersport 400 only), set the hard top exterior to the sophisto grey paint material.
		if (Configuration.isGreyHardTop(id as HardTopOptions)) {
			this._hardTopExterior.material = this.sophistoGreyPaintMaterial400;
		}

		// otherwise, set the hard top exterior to match the current body paint colour. n.b. this is skipped when there is no hard top,
		// as there is then nothing to colour, and it must not return early on failure either - the mesh visibility below has to run
		// whatever happens to the material, or a car with no hard top keeps the previous selection's meshes.
		else if (id !== HardTopOptions.None) {

			// get a material for the current body paint colour, which handles a bespoke colour having no paint description of its own.
			const material = cloneBodyPaintMaterial(this._paintDescriptions);
			if (material) {

				// set the hard top exterior material to match the body paint colour.
				this._hardTopExterior.material = material;
			}
		}

		// if any hard top is selected, show it, and hide the soft top meshes.
		if (id !== HardTopOptions.None) {
			Utilities.setActiveAll(this.hardTop, true);
			Utilities.setActiveAll(this.softTopUp, false);
			Utilities.setActiveAll(this.softTopDown, false);
		}

		// otherwise hide it and show the soft top with either hood up or down depending on hoodUp state.
		else {
			Utilities.setActiveAll(this.hardTop, false);
			Utilities.setActiveAll(this.softTopUp, hoodUp);
			Utilities.setActiveAll(this.softTopDown, !hoodUp);
		}
	}

	/**
	* Selects the side window state, putting them up or down by updating the relevant meshes/materials on the vehicle model.
	*/
	selectSideWindowState(setWindowsUp: boolean) {
		Utilities.setActiveAll(this.sideWindows, setWindowsUp);
	}

	/**
	* Selects a hood style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectHoodStyle(id: string) {
		
		// get the hood description for the given ID.
		const hoodDescription = this._hoodDescriptions.find(element => element.colourId === id);
		if (!hoodDescription) {
			console.error('No hood description found for id: ', id);
			return;
		}

		// smoothly fade between the current hood colour and the selected colour.
		this._fadeColour(this._softTopUp, hoodDescription.colour, 'albedoColor' as FadableKey);
		this._fadeColour(this._softTopDown, hoodDescription.colour, 'albedoColor' as FadableKey);
		this._fadeColour(this._exteriorSideScreen, hoodDescription.colour, 'albedoColor' as FadableKey);
	}
	
	selectStripeGraphicOption(id: string) {

		// hide the stripes if no option is selected, otherwise show them.
		if (id === 'NONE') {
			this.bonnetBootStripes400.active = false;
			return;
		} else {
			this.bonnetBootStripes400.active = true;
		}

		// get the s400 graphics desctiption for the given ID.
		const s400GraphicsDescriptions = this._s400GraphicsDescriptions.find(element => element.id === id);
		if (!s400GraphicsDescriptions) {
			console.error('No s400 graphics description found for id: ', id);
			return;
		}

		// set the relevant material.
		this._bonnetBootStripes400.material = s400GraphicsDescriptions.material?.clone();
	}

	selectSillGraphicOption(id: string) {

		// sill graphics are only available for the Supersport 400 model, so hide the sill graphics if not selected, and return.
		if (!Configuration.isSupersport400Model()) {
			this.sillGraphics400.active = false;
			return;
		}
		this.sillGraphics400.active = true;

		// get the s400 graphics description for the given ID.
		const s400GraphicsDescriptions = this._s400GraphicsDescriptions.find(element => element.id === id);
		if (!s400GraphicsDescriptions) {
			console.error('No s400 graphics description found for id: ', id);
			return;
		}

		// set the relevant material.
		this._sillGraphics400.material = s400GraphicsDescriptions.material?.clone();

		// if the coral sill graphic option is selected, also enable the rear diffuser graphic to match.
		if (id === Configuration.coralSillGraphicOption) {
			this.rearDiffuserGraphic400.active = true;
		}
		else {
			this.rearDiffuserGraphic400.active = false;
		}
	}

	selectDoorGraphicOption(id: string) {

		// hide the door roundel if no option is selected, otherwise show it.
		if (id === 'NONE') {
			this.doorRoundel.active = false;
			return;
		}
		else {
			this.doorRoundel.active = true;
		}

		// get the door roundel description for the given ID.
		const doorRoundelDescription = this._doorRoundelDescriptions.find(element => element.id === id);
		if (!doorRoundelDescription) {
			console.error('No door roundel description found for id: ', id);
			return;
		}

		// set the relevant material.
		this._doorRoundel.material = doorRoundelDescription.material?.clone();
	}

	selectAccentPackStyle(id: string) {

		// optionally select dark accent pack styles.
		if (id === AccentPackOptions.Dark) {
			this.sideScreenBlack.active = true;
			this.sideScreenSilver.active = false;

			this.cowlEmbelisherBlack.active = true;
			this.cowlEmbelisherSilver.active = false;

			this.rearOverridersBlack.active = true;
			this.rearOverridersSilver.active = false;

			this.frontOverridersBlack.active = true;
			this.frontOverridersSilver.active = false;
		}

		else {
			this.sideScreenBlack.active = false;
			this.sideScreenSilver.active = true;

			this.cowlEmbelisherBlack.active = false;
			this.cowlEmbelisherSilver.active = true;

			this.rearOverridersBlack.active = false;
			this.rearOverridersSilver.active = true;

			this.frontOverridersBlack.active = false;
			this.frontOverridersSilver.active = true;
		}
	}

	/**
	* Selects the front licence plate state (us Supersport only).
	*/
	selectFrontLicensePlateState(shouldDisplay: boolean) {
		if (Configuration.isSupersportModelInUsRegion()) {
			Utilities.setActiveAll(this.licensePlateUsFront, shouldDisplay);
		}
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Smoothly fades the colour of any material on the given mesh between the current colour and the given colour, using a tween animation.
	*/
	private _fadeColour(
		mesh: MeshComponent,
		targetColour: NumberArray,
		targetProperty: FadableKey
	) {
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		const material = mesh.material.clone() as FadableMaterial;
		mesh.material = material;

		const prop = targetProperty as string;

		const colourTween = new Tween({
			[prop]: material[targetProperty],
		});
		colourTween
			.to(
				{
					[prop]: targetColour
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				(material as Record<FadableKey, NumberArray>)[targetProperty] = c[prop];
			})
			.easing(Easing.Quadratic.InOut);

		this._tweenManager.start(colourTween);
	}
}
